/**
 * The farm discount rows shown on the policy Details screen, and the `segment.global.*` field each
 * one binds to (the same fields the offer-side farm-discount component writes).
 *
 * This list lives apart from `farm-discounts.jsx` because it is a contract with the GraphQL layer,
 * not just component config: every field here must also be selected by the segment `global` block
 * in `policy.query.fragments.js`, or the row reads `undefined` and reports a discount the policy
 * actually carries as not applied (#34739). `policy.query.fragments.test.js` asserts that, and it
 * needs the list without pulling in the component's brand-config / MobX / MUI module graph.
 *
 * Row semantics (all consumed by farm-discounts.jsx):
 *  - `enabledFlag`   the brand `appConfig.discounts` key gating availability (undefined = always
 *                    available), mirroring the offer gating in offer/hooks/use-discounts.jsx — a
 *                    discount the brand doesn't offer is omitted.
 *  - `entityGated`   only available to an Individual/Joint named insured (not LLC/Trust/etc.),
 *                    matching the offer side.
 *  - `hideForRental` #32516: Legacy/Military/Education apply a 1.0 (no-op) ClarionDoor factor on
 *                    Rental (Y), so they are omitted for a Rental policy. They stay on Farm (F) /
 *                    Home (Z).
 *  - `uwOnly`        #32231: only an underwriter (canApproveUWReview) may change it.
 *  - `derived`       #34635: the row reports what Clarion Door actually applied rather than the
 *                    `global` flag — see the `claimFreeActive` comment in farm-discounts.jsx.
 *  - `memberships`   the extra `global` fields the discount records beyond its own flag, applied
 *                    through a modal rather than a plain add/remove (#34739). Only MO Farm
 *                    Associations has them: the CFM rater's `farm_association` answer is the COUNT
 *                    of these, not the flag, so a row that set the flag alone printed "Included" on
 *                    the dec while the premium never moved.
 */
export const FARM_DISCOUNTS = [
  { field: 'multiPolicyDiscount', label: 'Auto/Home bundle discount', enabledFlag: 'hasAutoHomeBundleDiscount' },
  { field: 'legacyDiscount', label: 'Legacy discount', entityGated: true, hideForRental: true },
  { field: 'militaryDiscount', label: 'Military discount', entityGated: true, hideForRental: true },
  { field: 'educationDiscount', label: 'Education discount', entityGated: true, hideForRental: true },
  { field: 'cropInsuranceDiscount', label: 'Crop insurance discount', enabledFlag: 'hasCropInsuranceDiscount' },
  { field: 'traditionsDiscount', label: 'Traditions discount', enabledFlag: 'hasTraditionsDiscount' },
  {
    field: 'farmAssociationDiscount',
    label: 'Association discount',
    enabledFlag: 'hasAssociationDiscount',
    memberships: ['memberOfCattlemenAssociation', 'memberOfSoybeanAssociation']
  },
  // #32231: claim-free is underwriter-only — agents can view but not change it.
  // #34635: it is also DERIVED — see `claimFreeActive` in farm-discounts.jsx. It reports the savings
  // Clarion Door applied, not `global.claimFreeDiscount`, which is the UW override rather than the
  // discount's state. The field is still fetched and written: removing writes the `false` override.
  { field: 'claimFreeDiscount', label: 'Claim-free discount', uwOnly: true, derived: true }
];

/**
 * Extra `segment.global.*` fields the discounts card reads but that are not rows of their own:
 * `AutoBundleSelect` records WHICH carrier the bundled auto policy is with alongside
 * `multiPolicyDiscount`, and each row's `memberships` record what the discount applies to. Fetched
 * for the same reason as the fields above.
 */
export const FARM_DISCOUNT_SUPPORTING_GLOBAL_FIELDS = [
  'autoBundleCarrier',
  ...FARM_DISCOUNTS.flatMap(({ memberships }) => memberships ?? [])
];
